<template>
<div class="card">
    <div class="card-header">
        <p class="card-header-title">
            {{ trans.element.profile }}
        </p>
    </div>

    <div class="card-content field-label-container">
        <div class="columns is-centered">
            <div class="column">
                <form :method="method"
                    :action="action"
                    id="profile_form"
                    @submit.prevent="save"
                    @input="clearValidation"
                    novalidate
                >
                    <slot><!--CSRF token--></slot>

                    <div class="field is-horizontal">
                        <label class="field-label"></label>
                        <div class="field-body">
                            <p class="has-text-weight-bold has-text-success" v-if="profileSaved">
                                {{ trans.msg.profile_saved }}
                            </p>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <label for="name" class="field-label is-normal">
                            {{ trans.element.name }}
                        </label>
                        <div class="field-body">
                            <div class="field">
                                <div class="control">
                                    <input id="name"
                                        class="input"
                                        :class="{ 'is-danger': errors.name }"
                                        type="text"
                                        name="name"
                                        autocomplete="name"
                                        autofocus
                                        required
                                        v-model="form.name"
                                    >
                                </div>
                                <p v-show="errors.name" class="help is-danger">
                                    {{ errors.name }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <label for="email" class="field-label is-normal">
                            {{ trans.element.email }}
                        </label>
                        <div class="field-body">
                            <div class="field">
                                <div class="control">
                                    <input id="email"
                                        class="input"
                                        :class="{ 'is-danger': errors.email }"
                                        type="email"
                                        name="email"
                                        autocomplete="email"
                                        required
                                        v-model="form.email"
                                       :disabled="block_update_email"
                                    >
                                </div>
                                <p v-show="block_update_email" class="help is-danger">
                                    {{ block_update_email_message }}
                                </p>
                                <p v-show="errors.email" class="help is-danger">
                                    {{ errors.email }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <label for="new_password" class="field-label is-normal">
                            {{ trans.element.new_password }}
                        </label>
                        <div class="field-body">
                            <div class="field">
                                <div class="control">
                                    <input id="new_password" class="input"
                                        :class="{ 'is-danger': errors.new_password }"
                                        type="password"
                                        name="new_password"
                                        :placeholder="trans.element.new_password_placeholder"
                                        autocomplete="new-password"
                                        v-model="form.new_password"
                                    >
                                </div>
                                <p v-show="errors.new_password" class="help is-danger">
                                    {{ errors.new_password }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <transition name="slide-fade">
                        <div class="field is-horizontal" v-if="form.new_password">
                            <label for="new_password_confirm" class="field-label is-normal">
                                {{ trans.element.new_confirm_password }}
                            </label>
                            <div class="field-body">
                                <div class="field">
                                    <div class="control">
                                        <input id="new_password_confirm"
                                            class="input"
                                            :class="{ 'is-danger': errors.new_password_confirmation }"
                                            type="password"
                                            name="new_password_confirmation"
                                            autocomplete="off"
                                            v-model="form.new_password_confirmation"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>

                    <div class="field button-container is-horizontal">
                        <div class="field-label"></div>
                        <div class="field-body">
                            <div class="field">
                                <div class="control">
                                    <button type="submit"
                                        class="button is-info"
                                        :class="{'is-loading': isLoading }"
                                        :disabled="isLoading || !isUpdateRequired"
                                    >
                                        {{ trans.element.save }}
                                    </button>
                                </div>
                            </div>
                            <p v-show="errors.unexpected" class="help is-danger">
                                {{ errors.unexpected }}
                            </p>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</div>
</template>

<script>
    import { axiosForm } from '../mixins/axiosFormMixin';
    import passwordVerifyMixin from "../mixins/passwordVerifyMixin";

    export default {
        mixins: [
            axiosForm,
            passwordVerifyMixin
        ],
        props: {
            user_data: Object,
            block_update_email: {
                type: Boolean,
                default: false,
            },
            block_update_email_message: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                form: {
                    name: this.user_data.name,
                    email: this.user_data.email,
                    new_password: null,
                    new_password_confirmation: null,
                },
                user: this.user_data,
                profileSaved: false,
                failReport: 'Ошибка при изменении профиля пользователя',
            }
        },
        computed: {
            /**
             * Определение наличия изменений в форме
             * @returns {boolean}
             */
            isUpdateRequired () {
                let nameChanged = this.form.name !== this.user.name,
                    emailChanged = this.form.email !== this.user.email,
                    passwordChanged = !!this.form.new_password;

                return nameChanged || emailChanged || passwordChanged;
            },
        },
        methods: {
            /**
             * Обработчик успешного подтверждения пароля
             */
            onVerifiedPassword() {
                this.save();
            },

            /**
             * Обработчик нажатия кнопки Сохранить в форме
             * @param e {Object} - событие
             */
            save(e) {
                this.profileSaved = false;

                let isUpdateRequired = this.isUpdateRequired,
                    isPasswordVerified = !!this.form.password;

                if (!isUpdateRequired) return;

                if (!isPasswordVerified) {
                    this.validate();
                    return;
                }

                this.submit();
            },

            /**
             * Валидация формы профиля
             */
            validate() {
                this.submit({}, this.action, 'options')
            },

            /**
             * Обработчик ответа axios запросов формы
             * @param response {object} - ответ сервера
             *
             * @returns void
             */
            postResultHandler(response) {
                // Если положительный ответ валидации
                if (response.data.validated) {
                    this.verifyPassword();
                }

                // Если положительный ответ изменения профиля
                if (response.data.result) {
                    let user = response.data.user,
                        passwordHasChanged = !!this.form.new_password,
                        emailHasChanged = this.user.email !== user.email;

                    this.user.name = user.name;
                    this.user.email = user.email;
                    this.form.new_password = null;
                    this.form.new_password_confirmation = null;

                    if (passwordHasChanged) {
                        this.form.password = null;
                    }

                    if (emailHasChanged) {
                        window.location.replace(this.cancel_route);
                    }

                    this.profileSaved = true;
                    sendGAEvent('Успешное изменение профиля пользователя');
                }
            },
        }
    };
</script>
